import { IServiceFactoryExtended } from '../service-factory-extended.interface';
import { ServiceBase } from '../ServiceBase';
import { IBecauseYouPlayedService } from './because-you-played.service.interface';
import { observable, action, makeObservable } from 'mobx';
import {GameInfo} from '../games/game-info';
import {Check} from "@sparkware/app-utils";
import {NullableUndefinedNumber} from "../../../common/types/nullable-types";
import {ALL_ARENAS} from '../arenas/arena-consts';

const BECAUSE_YOU_PLAYED_LAST_REAL_GAME_LOCAL_STORAGE_KEY = "because_you_played_last_played_real_game";

interface ILastPlayedGames {
  lastPlayedGame?: number; //last played game in general
  lastPlayedGameByArena: Record<string, number>; // key is arena name, value last played gameType on that arena
}


export class BecauseYouPlayedService extends ServiceBase implements IBecauseYouPlayedService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this._lastPlayedGames = this._getLastPlayedGamesFromLocalStorage();
    if(!this._lastPlayedGames.lastPlayedGameByArena) {
      this._lastPlayedGames.lastPlayedGameByArena = this._createDefaultPlayedGameByArenaObject();
    }

    this.services.gameRunner.then(gameRunner => {
      this.reaction(() => { return {
            lastPlayedRealGame: gameRunner.lastPlayedGame.lastPlayedRealGame,
          } },
          async (data) => {
            if (this.services.user.isAuthorized) {
              if (data.lastPlayedRealGame) {
                this._setLastPlayedGame(data.lastPlayedRealGame);
                await this._updateCurrentBecauseYouPlayedGames();
              }
            }
          }
      );
    })



    this.reaction(() => this.services.routing.currentRoute, 
      async () => {
        await this._updateCurrentBecauseYouPlayedGames();
      }, {
        fireImmediately: true
      }
    );
  }

  _createDefaultPlayedGameByArenaObject(): Record<string, number> {
    return ALL_ARENAS.reduce((accumulator, currentValue) => {
      accumulator[currentValue] = 0;
      return accumulator;
    }, {});
  }

  private _getLastPlayedGamesFromLocalStorage(): ILastPlayedGames {
    const lastPlayedGameType = parseInt(this.services.localStorage.getItem(BECAUSE_YOU_PLAYED_LAST_REAL_GAME_LOCAL_STORAGE_KEY));
    if(Check.isNumber(lastPlayedGameType)) {
      //This is for backward compatibility. Initially in local storage under the key BECAUSE_YOU_PLAYED_LAST_REAL_GAME_LOCAL_STORAGE_KEY there was only a game type
      return {
        lastPlayedGame: lastPlayedGameType,
        lastPlayedGameByArena: this._createDefaultPlayedGameByArenaObject()
      };
    } else {
      return this.services.localStorage.getJson(BECAUSE_YOU_PLAYED_LAST_REAL_GAME_LOCAL_STORAGE_KEY) || this._createEmptyLastPlayedGamesList();
    }
  }

  private _lastPlayedGames: ILastPlayedGames;

  private _setLastPlayedGame(gameInfo: GameInfo): void {
    Object.keys(this._lastPlayedGames.lastPlayedGameByArena).forEach(arenaName => {
      const arena = this.services.arenas.getArena(arenaName);
      if (0 < arena.filterGamesByArenaType([gameInfo]).length) {
        this._lastPlayedGames.lastPlayedGameByArena[arenaName] = gameInfo.gameType;
      }
    });

    const currentArena = this.services.arenas.getCurrentArena();
    this._lastPlayedGames.lastPlayedGameByArena[currentArena.arenaName] = gameInfo.gameType;
    this._lastPlayedGames.lastPlayedGame = gameInfo.gameType;


    this.services.localStorage.setJson(BECAUSE_YOU_PLAYED_LAST_REAL_GAME_LOCAL_STORAGE_KEY, this._lastPlayedGames);
  }

  private _createEmptyLastPlayedGamesList(): ILastPlayedGames {
    return {
      lastPlayedGameByArena: this._createDefaultPlayedGameByArenaObject()
    };
  }

  @observable.ref
  currentBecauseYouPlayedGames: GameInfo[] = [];

  @action.bound
  private _setCurrentBecauseYouPlayedGames(gamesInfo: GameInfo[]): void {
    this.currentBecauseYouPlayedGames = gamesInfo;
  }

  private _getLastPlayedGameTypeForArena(arenaName: string): NullableUndefinedNumber {
    let lastGamePlayedGameTypeForArena = this._lastPlayedGames.lastPlayedGameByArena[arenaName];

    if(lastGamePlayedGameTypeForArena) {
      return lastGamePlayedGameTypeForArena;
    }

    return this._lastPlayedGames.lastPlayedGame;
  }

  private async _updateCurrentBecauseYouPlayedGames(): Promise<void> {
    const currentArena = this.services.arenas.getCurrentArena();

    let lastGamePlayedForCurrentArena = this._getLastPlayedGameTypeForArena(currentArena.arenaName);

    //reset data first since we are currently changed the route, some data can be seen from previous route
    this._setCurrentBecauseYouPlayedGames([]);

    if(!lastGamePlayedForCurrentArena) {
      return;
    }

    const correlatedGames = await this.services.games.getCorrelatedGames(lastGamePlayedForCurrentArena, 50);
    let gamesInfo = (await this.services.games.getUnrestrictedGamesInfoArray([lastGamePlayedForCurrentArena, ...correlatedGames])) || [];
    gamesInfo = currentArena.filterGamesByArenaType(gamesInfo);

    if (gamesInfo.length > 1 && gamesInfo.find(g => g.gameType === lastGamePlayedForCurrentArena)) {
      this._setCurrentBecauseYouPlayedGames(gamesInfo);
    }
  }
}
